import type { MessageAttachment } from '@/types';

/**
 * Inline step pictures (2026-10-12). A bot reply may carry `[[media:K]]` markers where K is the 1-based position of a
 * picture in the message's own attachment list (the contract web chat, WhatsApp and email all use; the server renumbers
 * it). The reviewer of an email draft sees each picture where the customer will see it. A marker that points at nothing,
 * at a non-image, or that repeats an earlier one is removed - never shown as text.
 *
 * Mirrors Frontend/chat-widget/src/utils/inlineMedia.ts (the widget is a separate app, so the helper is copied, not shared).
 */

const MARKER = /\[\[\s*media\s*:\s*(\d+)\s*\]\]/gi;

// Private-use code points: they survive markdown and the sanitizer as plain text and cannot be typed by accident.
const OPEN = '\uE000';
const CLOSE = '\uE001';
const SENTINEL = new RegExp(`${OPEN}(\\d+)${CLOSE}`, 'g');

export interface PreparedInlineMedia {
  /** Text with every placeable marker replaced by a sentinel and every other marker removed. */
  text: string;
  /** 0-based indexes into the attachments array that were placed inline. */
  placed: number[];
}

export function hasInlineMarker(content: string | undefined | null): boolean {
  if (!content) return false;
  MARKER.lastIndex = 0;
  return MARKER.test(content);
}

function isImage(a: MessageAttachment): boolean {
  return (a.mimeType ?? '').toLowerCase().startsWith('image/');
}

export function prepareInlineMedia(content: string, attachments: MessageAttachment[] | undefined): PreparedInlineMedia {
  const list = attachments ?? [];
  const placed: number[] = [];
  const seen = new Set<number>();

  const text = content.replace(MARKER, (_m, digits: string) => {
    const index = Number.parseInt(digits, 10) - 1;
    const attachment = Number.isInteger(index) && index >= 0 ? list[index] : undefined;
    if (!attachment || !isImage(attachment) || seen.has(index)) return '';
    seen.add(index);
    placed.push(index);
    return `${OPEN}${index + 1}${CLOSE}`;
  });

  return { text, placed };
}

/** Turns sentinels in ALREADY-SANITIZED html into empty mount spans (digits only - the markup is ours). */
export function injectInlineMountPoints(sanitizedHtml: string): string {
  return sanitizedHtml.replace(
    SENTINEL,
    (_m, k: string) => `<span class="bx-inline-media" data-media-k="${Number.parseInt(k, 10)}"></span>`,
  );
}
